import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import MyProductInfo, { ProductItem } from './MyProductInfo';
import FeaturedDocumentation from './FeaturedDocumentation';
import { fromAccounts, fromTestCloudSubscriptions, fromTestOpsSubscriptions } from '../../store';
import {
  fromAccountUsers,
  fromStudioSubscriptions,
  fromTestOpsPlatformSubscriptions,
} from '../../store/rootReducer';
import {
  OrganizationFeature,
  TestCloudSubscription,
  TestOpsPlatformSubscription,
  TestOpsSubscriptionSource,
  TestOpsSubscriptionStatus,
} from '../../models';
import { getTestCloudFeature, getTestCloudFreeTrialExpiryDate } from '../subscription-management/utils';
import { useConfig } from '../../config';

const useStyles = makeStyles(() => ({
  boxFlexWrap: {
    display: 'column',
    width: '76%',
    flexWrap: 'wrap',
  },
}));

export interface ProductInfoProps {
  accountId: number;
  organizationId: number;
  hasPlatformSubscription: boolean;
  domain?: string;
}

const ProductInfo = (props: ProductInfoProps) => {
  const classes = useStyles();
  const { organizationId, accountId, hasPlatformSubscription, domain } = props;
  const { config } = useConfig();
  const account = useSelector(fromAccounts.selectAccountById(accountId));

  const lstStudioSubscription: ProductItem[] = useSelector(
    fromStudioSubscriptions.selectByAccountId(accountId),
  ).map(sub => ({
    id: sub.id,
    feature: sub.feature,
    quota: sub.quota,
    expiryDate: sub.expiryDate,
  }));

  const lstTestOpsSubscription: ProductItem[] = useSelector(
    fromTestOpsSubscriptions.selectByAccountId(accountId),
  ).map(sub => ({
    id: sub.id,
    feature: sub.feature,
    quota: sub.quota,
    source: sub.source,
    numberProject: account?.numberProjects,
    expiryDate: sub.expiryDate,
  }));

  const products = [...lstStudioSubscription,
    ...lstTestOpsSubscription,
  ];

  function calculateQuotaTestOpsPlatform(
    paidSubscription: TestOpsPlatformSubscription,
    trialRequestSubscription: TestOpsPlatformSubscription,
  ) {
    if (paidSubscription && trialRequestSubscription) {
      return paidSubscription.quota > trialRequestSubscription.quota
        ? paidSubscription.quota
        : trialRequestSubscription.quota;
    }
    if (paidSubscription) {
      return paidSubscription.quota;
    }
    if (trialRequestSubscription) {
      return trialRequestSubscription.quota;
    }
    return 0;
  }

  const list = useSelector(
    fromTestOpsPlatformSubscriptions.selectTestOpsPlatformSubscriptionByAccountId(accountId),
  );

  if (list.length > 0) {
    const trialRequestSubscription = list
      .filter(item => item.source === TestOpsSubscriptionSource.TRIAL_REQUEST)[0];
    const paidSubscription = list
      .filter(item => item.source === TestOpsSubscriptionSource.RECURLY)[0];
    const testOpsPlatformSubs = trialRequestSubscription || paidSubscription;
    const quotaPlatform = calculateQuotaTestOpsPlatform(paidSubscription, trialRequestSubscription);
    const testOpsPlatformSubscription: ProductItem = {
      id: testOpsPlatformSubs.id,
      feature: testOpsPlatformSubs.feature,
      quota: quotaPlatform,
      source: testOpsPlatformSubs.source,
      numberProject: account?.numberProjects,
      expiryDate: testOpsPlatformSubs.expiryDate,
    };
    products.push(testOpsPlatformSubscription);
  }

  const lstTestCloudSubscription: TestCloudSubscription[] = useSelector(
    fromTestCloudSubscriptions.selectByAccountId(accountId),
  );

  const testCloudSubscriptionCount = lstTestCloudSubscription
    .filter(subs => subs.status === TestOpsSubscriptionStatus.ACTIVE).length;

  const latestTestCloudSubscription = lstTestCloudSubscription[0];
  const feature = getTestCloudFeature(
    latestTestCloudSubscription,
    config,
    account?.createdAt,
  );

  const accountUser = useSelector(fromAccountUsers.findAccountUserByAccountId(accountId));

  if (feature) {
    if (testCloudSubscriptionCount === 0) {
      products.push({
        id: 0,
        feature: OrganizationFeature.TESTCLOUD_FREE_TRIAL,
        source: TestOpsSubscriptionSource.TRIAL_REQUEST,
        quota: 5,
        expiryDate: new Date(getTestCloudFreeTrialExpiryDate(config, account?.createdAt)),
      });
    } else {
      products.push({
        id: latestTestCloudSubscription.id,
        feature: latestTestCloudSubscription.feature,
        quota: latestTestCloudSubscription.concurrentSessions,
      });
    }
  }

  return (
    <Box className={classes.boxFlexWrap}>
      <MyProductInfo
        domain={domain}
        organizationId={organizationId}
        products={products}
        numberProjects={account?.numberProjects}
        accountUser={accountUser}
      />
      <FeaturedDocumentation hasPlatformSubscription={hasPlatformSubscription} />
    </Box>
  );
};

export default ProductInfo;
